import { z } from "zod";
import { AnimatedImageDto } from "./animated-image.ts";
import { ElementDto, EndSceneDto, StartSceneDto } from "./overlays.ts";
import { VoiceActorProvider } from "./voice-actor.ts";
import { AssetMediaAsyncDto } from "./asset-media.ts";
import { CaptionConfigDto } from "./caption.ts";
import { CompositionAspectRatioDto } from "./aspect-ratio.ts";
import { CreativeProjectTimelineItem } from "./creative-project.ts";
import { SourceImageDto } from "./source-image.ts";
import { TransitionDto } from "./transition.ts";
import { FontDto } from "./font.ts";
import { BackgroundMusicDto } from "./background-music.ts";
import { CreationSource } from "./analytics.ts";

/**
 * Composition caption format — references a caption preset by its external ID
 * and embeds the full resolved config (style, animation, position, etc.).
 */
export const CompositionCaptionDto = z.object({
  externalId: z.string(),
  config: CaptionConfigDto,
});

export const CompositionDto = z.object({
  fps: z.number(),
  aspectRatio: CompositionAspectRatioDto,
  autoDucking: z.boolean().optional(),
  captions: CompositionCaptionDto.optional(),
  mainSceneTimeline: z.array(CreativeProjectTimelineItem),
  voiceOver: z
    .object({
      externalId: z.string(),
      volume: z.number().optional(),
      startSilenceMs: z.number().optional(),
    })
    .optional(),
  voiceOverExternalIds: z.array(z.string()).optional(),
  backgroundMusic: z
    .object({
      externalId: z.string(),
      volume: z.number().optional(),
      startMs: z.number().optional(),
      fadeInMs: z.number().optional(),
      fadeOutMs: z.number().optional(),
      startSilenceMs: z.number().optional(),
      endSilenceMs: z.number().optional(),
    })
    .optional(),
  elements: z
    .record(
      z.string(), // aspect ratio
      z.array(ElementDto),
    )
    .optional(),
  startScene: z
    .record(
      z.string(), // aspect ratio
      StartSceneDto,
    )
    .optional(),
  endScene: z
    .record(
      z.string(), // aspect ratio
      EndSceneDto,
    )
    .optional(),
  startTransition: TransitionDto.optional(),
  endTransition: TransitionDto.optional(),
});

export const VideoDto = z.object({
  externalId: z.string(),
  compositionJson: CompositionDto,
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  hasOriginSourceImages: z.boolean(),
  folderExternalId: z.string().nullable(),
});

export const VoiceOverDto = z.object({
  externalId: z.string(),
  script: z.string(),
  voiceActorExternalId: z.string(),
  voiceActorProvider: VoiceActorProvider,
  src: z.string(),
  characterStartTimes: z.array(z.number()),
  createdAt: z.string(),
  durationMs: z.number(),
});

export const CreateVoiceOverDto = z.object({
  script: z.string(),
  voiceActorExternalId: z.string(),
});

export const GetVoiceOversByExtIdsDto = z.object({
  externalIds: z.array(z.string()),
});

export const GetVoiceOversByExtIdsResponseDto = z.array(VoiceOverDto);

export const CreateVideoDto = z.object({
  folderExternalId: z.string().nullable().optional(),
  source: CreationSource.optional(),
});

export const DuplicateVideoResponseDto = z.object({
  externalId: z.string(),
});

export const UpdateVideoDto = z.object({
  name: z.string(),
  compositionJson: CompositionDto,
});

export const FetchRealEstateImagesDto = z.object({
  listingUrl: z.string().url(),
});

export const FetchRealEstateImagesResponseDto = z.object({
  imageExternalIds: z.array(z.string()),
});

export const I2VCompositionProps = z.object({
  composition: CompositionDto,
  sourceImages: z.record(z.string(), SourceImageDto),
  animatedImages: z.record(z.string(), AnimatedImageDto),
  voiceOvers: z.array(VoiceOverDto),
  backgroundMusics: z.array(BackgroundMusicDto),
  assetMedias: z.record(z.string(), AssetMediaAsyncDto),
  durationInFrames: z.number(),
  fps: z.number(),
  isStudio: z.boolean().optional(),
  isPlaying: z.boolean().optional(),
  isFullscreen: z.boolean().optional(),
  fonts: z.record(z.string(), FontDto).optional(),
});

export type CompositionDto = z.infer<typeof CompositionDto>;
export type CompositionCaptionDto = z.infer<typeof CompositionCaptionDto>;
export type VideoDto = z.infer<typeof VideoDto>;
export type VoiceOverDto = z.infer<typeof VoiceOverDto>;
export type CreateVoiceOverDto = z.infer<typeof CreateVoiceOverDto>;
export type GetVoiceOversByExtIdsDto = z.infer<typeof GetVoiceOversByExtIdsDto>;
export type GetVoiceOversByExtIdsResponseDto = z.infer<
  typeof GetVoiceOversByExtIdsResponseDto
>;
export type CreateVideoDto = z.infer<typeof CreateVideoDto>;
export type DuplicateVideoResponseDto = z.infer<
  typeof DuplicateVideoResponseDto
>;
export type UpdateVideoDto = z.infer<typeof UpdateVideoDto>;
export type I2VCompositionProps = z.infer<typeof I2VCompositionProps>;
export type FetchRealEstateImagesDto = z.infer<typeof FetchRealEstateImagesDto>;
export type FetchRealEstateImagesResponseDto = z.infer<
  typeof FetchRealEstateImagesResponseDto
>;
